import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import BootstrapTable from "react-bootstrap-table-next"
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider, {
  Search,
} from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import { formatDate } from "helpers/misc_helpers"
import {
  GetPartnerCockpits,
  CreatePartnerCockpit,
} from "helpers/backend_helper"

const PartnerCockpitList = ({ t }) => {
  const [partnerCockpits, setPartnerCockpits] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [newCockpitName, setNewCockpitName] = useState("")

  useEffect(() => {
    fetchPartnerCockpits()
  }, [])

  const fetchPartnerCockpits = async () => {
    try {
      setLoading(true)
      const response = await GetPartnerCockpits()
      setPartnerCockpits(response)
    } catch (error) {
      alert(t("Failed to fetch partner cockpits"))
      console.error("Error fetching partner cockpits:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleModal = () => setModal(!modal)

  const handleCreateCockpit = async () => {
    if (!newCockpitName.trim()) {
      alert(t("Please enter a name"))
      return
    }

    try {
      await CreatePartnerCockpit({ name: newCockpitName })
      setNewCockpitName("")
      toggleModal()
      fetchPartnerCockpits() // Refresh the list
    } catch (error) {
      alert(t("Failed to create partner cockpit"))
      console.error("Error creating partner cockpit:", error)
    }
  }

  const getRoleBadgeColor = role => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "danger"
      case "user":
        return "primary"
      case "moderator":
        return "warning"
      default:
        return "secondary"
    }
  }

  const columns = [
    {
      dataField: "name",
      text: t("Name"),
      sort: true,
    },
    {
      dataField: "role",
      text: t("Role"),
      sort: true,
      formatter: cell => (
        <Badge color={getRoleBadgeColor(cell)} className="font-size-12">
          {cell}
        </Badge>
      ),
    },
    {
      dataField: "createdAt",
      text: t("Created At"),
      sort: true,
      formatter: cell => <>{formatDate(cell)}</>,
      headerStyle: _ => ({
        textAlign: "center",
      }),
      align: () => "center",
    },
    {
      dataField: "id",
      text: "",
      formatter: (cellContent, row) => (
        <div className="d-flex justify-content-end">
          <Link to={`/partner-cockpit/${row.id}`} className="btn btn-primary">
            {t("View Details")}
          </Link>
        </div>
      ),
    },
  ]

  const pageOptions = {
    sizePerPage: 10,
    totalSize: partnerCockpits.length,
    custom: true,
  }

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("Loading...")}</span>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardBody>
          <PaginationProvider
            pagination={paginationFactory(pageOptions)}
            keyField="id"
            columns={columns}
            data={partnerCockpits}
          >
            {({ paginationProps, paginationTableProps }) => (
              <ToolkitProvider
                keyField="id"
                data={partnerCockpits}
                columns={columns}
                bootstrap4
                search
              >
                {toolkitProps => (
                  <React.Fragment>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4 className="mb-0">{t("Partner Cockpits")}</h4>
                      <Button color="primary" onClick={toggleModal}>
                        {t("Create New")}
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <BootstrapTable
                        {...toolkitProps.baseProps}
                        {...paginationTableProps}
                        bordered={false}
                        striped
                        hover
                        classes={"table align-middle table-nowrap"}
                        headerWrapperClasses={"table-light"}
                      />
                      {partnerCockpits.length === 0 && (
                        <p className="text-center">
                          {t("No partner cockpits available")}
                        </p>
                      )}
                    </div>
                    <div className="pagination pagination-rounded justify-content-end mb-2">
                      <PaginationListStandalone {...paginationProps} />
                    </div>
                  </React.Fragment>
                )}
              </ToolkitProvider>
            )}
          </PaginationProvider>
        </CardBody>
      </Card>

      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {t("Create New Partner Cockpit")}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="cockpitName">{t("Name")}</Label>
              <Input
                type="text"
                name="cockpitName"
                id="cockpitName"
                value={newCockpitName}
                onChange={e => setNewCockpitName(e.target.value)}
                placeholder={t("Enter partner cockpit name")}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            {t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleCreateCockpit}>
            {t("Create")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default withTranslation()(PartnerCockpitList)
